import { useMemo, useState } from 'react'
import { useStats } from '../api'
import { useLang } from '../lang'
import { shortDate, ago, percent, unitOf } from '../time'
import type { Stats, StatsSpeciesRow, StatsBucket } from '../types'
import { Heading, Kicker, Label, SegmentedControl, StateLine } from '../design'
import type { SegOption } from '../design'
import * as st from './stats.css'

// Stats' OWN range options (independent of Live, brief §6.2). A week is the default.
const RANGES: SegOption<number>[] = [
  { value: 24, label: '24H' },
  { value: 168, label: '7D' },
  { value: 720, label: '30D' },
  { value: 1_000_000, label: 'ALL' },
]
const RANGE_PHRASE: Record<number, [string, string]> = {
  24: ['Past 24 hours', 'Le 24 uair'],
  168: ['Past 7 days', 'Le 7 lá'],
  720: ['Past 30 days', 'Le 30 lá'],
  1_000_000: ['All time', 'Gach am'],
}

type SortKey = 'count' | 'change' | 'first' | 'last' | 'name'

export function StatsTab({
  onSelect,
  windowHours,
  onWindow,
}: {
  onSelect: (sci: string) => void
  windowHours: number
  onWindow: (hours: number) => void
}) {
  const { data, isLoading, isError } = useStats(windowHours)
  const { lang, t } = useLang()
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'count', dir: -1 })

  const other = lang === 'ga' ? 'en' : 'ga'
  const primary = (en: string, ga: string | null) => (lang === 'ga' && ga ? ga : en)
  const gloss = (en: string, ga: string | null) => (lang === 'ga' ? (ga ? en : null) : ga)
  const nameOf = (r: StatsSpeciesRow) => primary(r.en, r.ga)

  const rows = useMemo(() => {
    if (!data) return []
    const s = [...data.species]
    const { key, dir } = sort
    s.sort((a, b) => {
      let d = 0
      if (key === 'count') d = a.count - b.count
      else if (key === 'change') d = (a.change ?? -Infinity) - (b.change ?? -Infinity)
      else if (key === 'first') d = String(a.first_time ?? '').localeCompare(String(b.first_time ?? ''))
      else if (key === 'last') d = String(a.last_time ?? '').localeCompare(String(b.last_time ?? ''))
      else d = nameOf(a).localeCompare(nameOf(b), lang === 'ga' ? 'ga' : 'en')
      return d * dir
    })
    return s
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sort, lang])

  // Loading is "…" and a failed load is "—", the same two marks About, the Journal and Live use.
  // This surface showed the busy ellipsis for both, so a report that could not be fetched read as
  // one still arriving (§8).
  if (isLoading || !data) return <StateLine busy={!isError}>{isError ? '—' : '…'}</StateLine>

  const phrase = RANGE_PHRASE[windowHours] ?? ['This period', 'An tréimhse seo']
  const rangeLabel =
    data.all_time || !data.range.from
      ? t(phrase[0], phrase[1])
      : `${t(phrase[0], phrase[1])} · ${shortDate(data.range.from, lang)} – ${shortDate(data.range.to, lang)}`

  const cov = data.coverage
  const covPct = cov.known && cov.fraction != null ? percent(cov.fraction) : null
  const cmp = data.comparison

  const onSortClick = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: (prev.dir * -1) as 1 | -1 } : { key, dir: key === 'name' ? 1 : -1 },
    )
  const ariaSort = (key: SortKey): 'ascending' | 'descending' | undefined =>
    sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined

  return (
    <section>
      {/* Period header — the report is ABOUT this window, not lifetime bragging. */}
      <header className={st.head}>
        <div>
          <Heading level="2xl">{t('Station report', 'Tuairisc an stáisiúin')}</Heading>
          <Kicker className={st.rangeLabel}>{rangeLabel}</Kicker>
        </div>
        <SegmentedControl
          options={RANGES}
          value={windowHours}
          onChange={onWindow}
          ariaLabel={t('Stats period', 'Tréimhse')}
        />
      </header>

      {/* Summary strip — figures + coverage + comparison (never KPI cards). */}
      <div className={st.summary}>
        <SumItem fig={data.totals.detections.toLocaleString()} label={t('Detections', 'Aimsithe')} />
        <SumItem fig={data.totals.species.toLocaleString()} label={t('Species', 'Speicis')} />
        {cov.known && covPct != null && (
          <SumItem fig={covPct} label={t('Coverage', 'Clúdach')} sub={coverageSub(cov, t)} />
        )}
        {cmp &&
          (cmp.comparable ? (
            <SumItem
              fig={signed(cmp.detections_change)}
              label={t('vs previous', 'i gcomparáid')}
              deltaDir={cmp.detections_change > 0 ? 'up' : cmp.detections_change < 0 ? 'down' : 'flat'}
            />
          ) : (
            <SumItem
              fig="—"
              label={t('vs previous', 'i gcomparáid')}
              sub={t('coverage too thin', 'clúdach ró-thanaí')}
            />
          ))}
      </div>

      {data.activity.buckets.length > 0 && <ActivityChart buckets={data.activity.buckets} />}

      {rows.length > 0 ? (
        <div className={st.tableWrap}>
          <table className={st.table}>
            <thead>
              <tr>
                <th className={st.th}>
                  <button
                    className={st.thBtn}
                    aria-sort={ariaSort('name')}
                    onClick={() => onSortClick('name')}
                  >
                    {t('Species', 'Speiceas')}
                  </button>
                </th>
                <th className={`${st.th} ${st.thNum}`}>
                  <button
                    className={st.thBtn}
                    aria-sort={ariaSort('count')}
                    onClick={() => onSortClick('count')}
                  >
                    {t('Count', 'Líon')}
                  </button>
                </th>
                <th className={`${st.th} ${st.thNum}`}>
                  <span className={st.thLabel}>{t('Share', 'Sciar')}</span>
                </th>
                <th className={`${st.th} ${st.thNum}`}>
                  <button
                    className={st.thBtn}
                    aria-sort={ariaSort('change')}
                    onClick={() => onSortClick('change')}
                  >
                    {t('Change', 'Athrú')}
                  </button>
                </th>
                <th className={st.th}>
                  <button
                    className={st.thBtn}
                    aria-sort={ariaSort('first')}
                    onClick={() => onSortClick('first')}
                  >
                    {t('First', 'Céad')}
                  </button>
                </th>
                <th className={st.th}>
                  <button
                    className={st.thBtn}
                    aria-sort={ariaSort('last')}
                    onClick={() => onSortClick('last')}
                  >
                    {t('Last heard', 'Cloiste')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.sci} className={st.row} onClick={() => onSelect(r.sci)}>
                  <td className={st.td}>
                    <span className={st.nameCell}>
                      <span className={st.name}>{primary(r.en, r.ga)}</span>
                      {gloss(r.en, r.ga) && (
                        <em lang={other} className={st.nameGloss}>
                          {gloss(r.en, r.ga)}
                        </em>
                      )}
                    </span>
                  </td>
                  <td className={st.tdNum}>{r.count.toLocaleString()}</td>
                  <td className={st.tdNum}>
                    <span className={st.shareBarWrap}>
                      <span className={st.tdMeta}>{percent(r.share)}</span>
                      <span className={st.shareBar}>
                        <span className={st.shareFill} style={{ width: percentWidth(r.share) }} />
                      </span>
                    </span>
                  </td>
                  <td className={st.tdNum}>
                    <ChangeCell change={r.change} />
                  </td>
                  <td className={st.td}>
                    <span className={st.tdMeta}>{r.first_time ? shortDate(r.first_time, lang) : '—'}</span>
                  </td>
                  <td className={st.td}>
                    <span className={st.tdMeta}>{r.last_time ? ago(r.last_time, lang) : '—'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <StateLine>{t('Nothing heard in this period.', 'Faic cloiste sa tréimhse seo.')}</StateLine>
      )}

      {/* Lifetime record — present, but quiet and last (brief §6.6). */}
      <div className={st.lifetime}>
        <LifeItem fig={data.lifetime.species_logged} label={t('Species logged', 'Speicis logáilte')} />
        <LifeItem
          fig={data.lifetime.detections_all_time}
          label={t('Detections all-time', 'Aimsithe san iomlán')}
        />
        <LifeItem fig={data.lifetime.days_listening} label={t('Days listening', 'Laethanta ag éisteacht')} />
      </div>
    </section>
  )
}

function SumItem({
  fig,
  label,
  sub,
  deltaDir,
}: {
  fig: string
  label: string
  sub?: string | null
  deltaDir?: 'up' | 'down' | 'flat'
}) {
  return (
    <div className={st.sumItem}>
      <span className={`${st.sumFig} ${deltaDir ? st.delta[deltaDir] : ''}`}>{fig}</span>
      <Label>{label}</Label>
      {/* The coverage note is FIGURES ("0 of 95 h listened · 74 h unknown"), so it takes the
          reading voice rather than a second, one-pixel-smaller copy of the label above it. */}
      {sub && <span className={st.sumSub}>{sub}</span>}
    </div>
  )
}

function LifeItem({ fig, label }: { fig: number; label: string }) {
  return (
    <div className={st.lifeItem}>
      <span className={st.lifeFig}>{fig.toLocaleString()}</span>
      <Label>{label}</Label>
    </div>
  )
}

function ChangeCell({ change }: { change: number | null }) {
  if (change == null) return <span className={st.tdMeta}>—</span>
  const dir = change > 0 ? 'up' : change < 0 ? 'down' : 'flat'
  return <span className={`${st.changeVal} ${st.delta[dir]}`}>{signed(change)}</span>
}

function ActivityChart({ buckets }: { buckets: StatsBucket[] }) {
  const max = Math.max(1, ...buckets.map((b) => b.count))
  const step = Math.max(1, Math.ceil(buckets.length / 8))
  return (
    <div className={st.chart}>
      <div className={st.bars}>
        {buckets.map((b) => {
          const variant = b.covered === false ? 'uncovered' : b.covered == null ? 'unknown' : 'covered'
          const h = Math.round((b.count / max) * 100)
          return (
            <div
              key={b.key}
              className={st.barCol}
              title={`${b.label} · ${b.count}${variant === 'covered' ? '' : variant === 'uncovered' ? ' · off the air' : ' · coverage unknown'}`}
            >
              {b.count > 0 ? (
                <span className={st.bar[variant]} style={{ height: `${Math.max(h, 2)}%` }} />
              ) : variant !== 'covered' ? (
                <span className={st.gapMark[variant === 'uncovered' ? 'uncovered' : 'unknown']} />
              ) : null}
            </div>
          )
        })}
      </div>
      <div className={st.axis}>
        {buckets.map((b, i) => (
          <span key={b.key} className={st.tick}>
            {i % step === 0 ? b.label : ''}
          </span>
        ))}
      </div>
    </div>
  )
}

function coverageSub(cov: Stats['coverage'], t: (en: string, ga: string) => string): string {
  const live = cov.hours_live ?? 0
  const known = cov.hours_known ?? 0
  const unknown = cov.hours_unknown ?? 0
  const base = t(`${live} of ${unitOf(known, 'h')} listened`, `${live} as ${unitOf(known, 'u')} éisteacht`)
  return unknown > 0
    ? `${base} · ${t(`${unitOf(unknown, 'h')} unknown`, `${unitOf(unknown, 'u')} anaithnid`)}`
    : base
}

// The bar's length, as a raw CSS percentage — `percent()` now reads "<1%" for a share that
// rounds to nothing, and that is a sentence, not a width.
function percentWidth(fraction: number): string {
  return `${Math.min(100, Math.max(0, fraction * 100)).toFixed(2)}%`
}

function signed(n: number): string {
  return n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString()
}
